import type { AuthProvider } from "@refinedev/core";

import { api, ApiError, tokens, type User } from "../api/client";

/** Refine auth provider over `/auth/*` with `X-Client: admin` (04 §2). */
export const authProvider: AuthProvider = {
  login: async ({ email, password }: { email: string; password: string }) => {
    try {
      const { data } = await api<{ access_token: string; refresh_token: string; user: User }>("/auth/login", {
        method: "POST",
        auth: false,
        body: { email, password },
      });
      tokens.set(data.access_token, data.refresh_token);
      return { success: true, redirectTo: "/" };
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.code === "invalid_credentials"
            ? "Wrong email or password."
            : e.code === "forbidden"
              ? "This account cannot use the admin panel."
              : e.message
          : "Could not reach the server.";
      return { success: false, error: { name: "LoginError", message } };
    }
  },

  logout: async () => {
    try {
      await api("/auth/logout", { method: "POST", body: { refresh_token: tokens.refresh } });
    } catch {
      // local sign-out regardless
    }
    tokens.clear();
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    if (!tokens.access) return { authenticated: false, redirectTo: "/login" };
    try {
      await api<User>("/me");
      return { authenticated: true };
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        tokens.clear();
        return { authenticated: false, redirectTo: "/login" };
      }
      // offline: keep the session
      return { authenticated: true };
    }
  },

  getIdentity: async () => {
    const { data } = await api<User>("/me");
    return { id: data.id, name: data.name, email: data.email, role: data.role, vendorId: data.vendor_id };
  },

  getPermissions: async () => {
    const { data } = await api<User>("/me");
    return data.role;
  },

  onError: async (error) => {
    if (error instanceof ApiError && error.status === 401) {
      tokens.clear();
      return { logout: true, redirectTo: "/login" };
    }
    return {};
  },
};
